import { CircleAlert } from "lucide-react";

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="21" cy="21" r="20.5" stroke="#dfdbde" />
      <path d="M21 13V23M21 27V29" stroke="#762a5c" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  loading?: boolean;
  tone?: "danger" | "default";
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  loading = false,
  tone = "default",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  const isDanger = tone === "danger";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center max-[767px]:px-[5%]">
      <div
        className="absolute inset-0 z-[-1] bg-black/25"
        onClick={onCancel}
      />

      <div role="alertdialog" aria-modal="true" className="relative w-full max-w-[30rem] overflow-hidden rounded-[1.2rem] border border-[#dfdbde] bg-[#fafafa] shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-[991px]:w-[90%] max-[991px]:max-w-none">
        {/* Header */}
        <div className="flex items-center gap-2 px-6 pt-6 pb-4">
          <CircleAlert className="h-[1.6rem] w-[1.6rem] shrink-0 text-[#762a5c]" />
          <h2 className="m-0 font-[family-name:var(--font-display)] text-[1.25rem] font-medium leading-[1.2] text-[#321b29]">
            {title}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          <p className="m-0 text-[0.93rem] leading-[1.55] text-[#7f777d]">
            {description}
          </p>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-3 border-t border-[#ebe7ea] px-6 py-5 max-[767px]:flex max-[767px]:flex-col">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-[0.9rem] border border-[#ddd9dc] bg-white px-6 py-3.5 text-[0.93rem] font-semibold leading-none text-[#2a2227] transition-transform duration-200 hover:-translate-y-[3%] active:scale-95"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-[0.9rem] px-6 py-3.5 text-[0.93rem] font-semibold leading-none text-white transition-transform duration-200 hover:-translate-y-[3%] active:scale-95 disabled:opacity-55 ${
              isDanger ? "bg-[#762a5c]" : "bg-[#762a5c]"
            }`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Please wait..." : confirmLabel}
          </button>
        </div>

        {/* Close button */}
        <button
          type="button"
          className="absolute right-3 top-3 flex text-[#7f777d] hover:text-[#321b29] transition-colors"
          aria-label="Close"
          onClick={onCancel}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}
